import { usePermissions } from '@cobliteam/permissions';
import diacritics from 'diacritics';
import Fuse from 'fuse.js';
import { useCallback, useEffect, useMemo } from 'react';
import { useGetDevices } from '../useEntity/useDevices';
import useDriver from '../useEntity/useDriver';
import { useGetAllGeofences } from '../useEntity/useGeofenceV3';
import useVehicle from '../useEntity/useVehicle';
import useVehicleGroup from '../useEntity/useVehicleGroup';
import useGlobalFilterStore from '../useGlobalFilterStore';
import type {
  MentionableEntity,
  MentionableEntityType,
  SearchCategoryId,
  SearchComposableOptions,
  SearchDataset,
  SearchEntityWithCategory,
  SearchItemsResult,
  SearchOptions,
  SearchPageWithCategory,
  SearchStatus,
} from './types';
import useSearchStore from './useSearchStore';
import { buildSearchDataset, DEFAULT_MENTIONABLE_ENTITY_TYPES, EMPTY_SEARCH_DATASET, getMentionableEntities, getSearchDatasetKey, normalizeSearchOptions } from './utils';

const getQueryError = (errors: unknown[]) => {
  return errors.find(Boolean);
};

const getStoreDataset = (dataset?: SearchDataset, fallback?: SearchDataset) => {
  if (dataset) return dataset;
  return fallback ?? EMPTY_SEARCH_DATASET;
};

export const useSearchData = <TCategory = never, TPage extends SearchPageWithCategory<TCategory> = never>(options?: SearchOptions & SearchComposableOptions<TCategory, TPage>) => {
  const vehicleGroupIdsFromStore = useGlobalFilterStore((state) => state.vehicleGroupIds);
  const { isAccessAllowed } = usePermissions();

  const resolvedOptions = normalizeSearchOptions({
    ...options,
    vehicleGroupIds: options?.vehicleGroupIds ?? vehicleGroupIdsFromStore,
    includeObservationPeriodTrigger: options?.includeObservationPeriodTrigger ?? (options?.currentLocation === '/monitoring' || options?.currentLocation === '/devices'),
    includeGeofences: options?.includeGeofences ?? isAccessAllowed('view:places-of-interest'),
  });
  const datasetKey = getSearchDatasetKey(resolvedOptions);

  const datasetFromStore = useSearchStore((state) => state.datasetsByKey[datasetKey]);
  const status = useSearchStore((state) => state.statusByKey[datasetKey] ?? 'idle');
  const error = useSearchStore((state) => state.errorsByKey[datasetKey]);
  const lastUpdatedAt = useSearchStore((state) => state.lastUpdatedAtByKey[datasetKey]);
  const setDataset = useSearchStore((state) => state.setDataset);
  const setLoading = useSearchStore((state) => state.setLoading);
  const setError = useSearchStore((state) => state.setError);
  const invalidateDataset = useSearchStore((state) => state.invalidateDataset);
  const clearDataset = useSearchStore((state) => state.clearDataset);

  const queriesEnabled = options?.enabled !== false;
  const vehiclesQuery = useVehicle().getVehicles({ enabled: queriesEnabled }, resolvedOptions.vehicleGroupIds);
  const driversQuery = useDriver().getDriversWithVehicles({ options: { enabled: queriesEnabled } });
  const vehicleGroupsQuery = useVehicleGroup().getVehicleGroups({ enabled: queriesEnabled }, resolvedOptions.vehicleGroupIds);
  const devicesQuery = useGetDevices({
    vehicleGroupIds: resolvedOptions.vehicleGroupIds,
    includeObservationPeriodTrigger: resolvedOptions.includeObservationPeriodTrigger,
    options: { enabled: queriesEnabled },
  });
  const geofencesQuery = useGetAllGeofences({ options: { enabled: queriesEnabled && resolvedOptions.includeGeofences } });

  const hasPendingQueries =
    vehiclesQuery.isPending || driversQuery.isPending || vehicleGroupsQuery.isPending || devicesQuery.isPending || (resolvedOptions.includeGeofences && geofencesQuery.isPending);
  const hasRefetchingQueries =
    vehiclesQuery.isRefetching ||
    driversQuery.isRefetching ||
    vehicleGroupsQuery.isRefetching ||
    devicesQuery.isRefetching ||
    (resolvedOptions.includeGeofences && geofencesQuery.isRefetching);
  const queryError = getQueryError([vehiclesQuery.error, driversQuery.error, vehicleGroupsQuery.error, devicesQuery.error, geofencesQuery.error]);

  const normalizedDataset = useMemo(
    () =>
      buildSearchDataset({
        vehicles: vehiclesQuery.data,
        drivers: driversQuery.data,
        vehicleGroups: vehicleGroupsQuery.data,
        devices: devicesQuery.data,
        geofences: resolvedOptions.includeGeofences ? geofencesQuery.data : [],
      }),
    [vehiclesQuery.data, driversQuery.data, vehicleGroupsQuery.data, devicesQuery.data, resolvedOptions.includeGeofences, geofencesQuery.data]
  );

  useEffect(() => {
    if (queryError) {
      setError(datasetKey, queryError);
      return;
    }

    if (hasPendingQueries) {
      setLoading(datasetKey);
      return;
    }

    if (hasRefetchingQueries) {
      setLoading(datasetKey, { isRefetching: true });
      return;
    }

    setDataset(datasetKey, normalizedDataset);
  }, [datasetKey, hasPendingQueries, hasRefetchingQueries, normalizedDataset, queryError, setDataset, setError, setLoading]);

  const refresh = useCallback(async () => {
    invalidateDataset(datasetKey);

    await Promise.all([
      vehiclesQuery.refetch(),
      driversQuery.refetch(),
      vehicleGroupsQuery.refetch(),
      devicesQuery.refetch(),
      ...(resolvedOptions.includeGeofences ? [geofencesQuery.refetch()] : []),
    ]);
  }, [datasetKey, devicesQuery, driversQuery, geofencesQuery, invalidateDataset, resolvedOptions.includeGeofences, vehicleGroupsQuery, vehiclesQuery]);

  const dataset = getStoreDataset(datasetFromStore, queryError || hasPendingQueries ? undefined : normalizedDataset);
  const hasError = Boolean(queryError || error);
  const canComposeSearchItems = Boolean(options?.pagesData && options.getCategory);
  const getRequiredCategory = useCallback(
    (categoryId: SearchCategoryId): NonNullable<TCategory> => {
      const category = options?.getCategory?.(categoryId);

      if (!category) {
        throw new Error(`Missing search category for "${categoryId}"`);
      }

      return category as NonNullable<TCategory>;
    },
    [options]
  );

  const items = useMemo<SearchItemsResult<TCategory, TPage>['items']>(() => {
    if (!(options?.pagesData && options.getCategory)) return [];

    const vehiclesWithCategory = dataset.vehicles.map(
      (vehicle) =>
        ({
          ...vehicle,
          category: getRequiredCategory('vehicles'),
        }) satisfies SearchEntityWithCategory<(typeof dataset.vehicles)[number], TCategory>
    );
    const driversWithCategory = dataset.drivers.map(
      (driver) =>
        ({
          ...driver,
          category: getRequiredCategory('drivers'),
        }) satisfies SearchEntityWithCategory<(typeof dataset.drivers)[number], TCategory>
    );
    const vehicleGroupsWithCategory = dataset.vehicleGroups.map(
      (vehicleGroup) =>
        ({
          ...vehicleGroup,
          category: getRequiredCategory('vehicleGroups'),
        }) satisfies SearchEntityWithCategory<(typeof dataset.vehicleGroups)[number], TCategory>
    );
    const devicesWithCategory = dataset.devices.map(
      (device) =>
        ({
          ...device,
          category: getRequiredCategory('devices'),
        }) satisfies SearchEntityWithCategory<(typeof dataset.devices)[number], TCategory>
    );
    const geofencesWithCategory = dataset.geofences.map(
      (geofence) =>
        ({
          ...geofence,
          category: getRequiredCategory('geofences'),
        }) satisfies SearchEntityWithCategory<(typeof dataset.geofences)[number], TCategory>
    );

    return [...vehiclesWithCategory, ...driversWithCategory, ...vehicleGroupsWithCategory, ...devicesWithCategory, ...geofencesWithCategory, ...options.pagesData];
  }, [dataset, getRequiredCategory, options?.getCategory, options?.pagesData]);

  const fuse = useMemo(() => {
    if (!canComposeSearchItems) return undefined;

    return new Fuse(items, {
      getFn: (obj: unknown, path: string | string[]) => {
        const value = Fuse.config.getFn(obj, path);
        if (typeof value === 'string') {
          return diacritics.remove(value);
        }
        return value;
      },
      keys: [
        'title',
        {
          name: 'primary',
          weight: 1,
        },
        'description',
        'label',
        'id',
        'licensePlate',
        'alias',
        'cobliId',
        'tagsIndex',
        'name',
        'driverCode',
        'driverLicense',
        'registryId',
        'cpf',
        'identifier',
        'vehicles.label',
        'vehicles.id',
        'vehicles.licensePlate',
        'vehicles.alias',
        'vehicles.cobliId',
        'deviceId',
        'deviceCode',
        'deviceType',
        'address',
      ],
    });
  }, [canComposeSearchItems, items]);

  return {
    datasetKey,
    dataset,
    items,
    fuse,
    error: queryError ?? error,
    hasError,
    status: status as SearchStatus,
    isLoading: hasPendingQueries || status === 'loading',
    isRefetching: hasRefetchingQueries || status === 'refetching',
    lastUpdatedAt,
    refresh,
    invalidateDataset: () => invalidateDataset(datasetKey),
    clearDataset: () => clearDataset(datasetKey),
  };
};

export const useMentionableEntities = (options?: Omit<SearchOptions, 'includeGeofences'> & { entityTypes?: MentionableEntityType[] }) => {
  const entityTypes = options?.entityTypes?.length ? options.entityTypes : DEFAULT_MENTIONABLE_ENTITY_TYPES;
  const searchData = useSearchData({
    ...options,
    includeGeofences: entityTypes.includes('geofences'),
  });

  const entities = useMemo<MentionableEntity[]>(() => getMentionableEntities(searchData.dataset, entityTypes), [entityTypes, searchData.dataset]);

  return {
    ...searchData,
    entities,
  };
};
